<script type='text/jsx'>
import _ from 'lodash';
import { mapActions, mapMutations, mapGetters } from 'vuex';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import {
  autoAddTypes,
  BILLING_CYCLE, isEventSaasOrEventSassCore,
  planTypes,
  TrackingBillingCycleKeys, TrackingBillingCycleUnitKeys,
  trackingPlanTypes,
} from '@/common/constants';
import { isEmptyVal, noopPromise } from '@/utils';
import SaveCount from '@/components/save-count';
import ConfigCycleV2 from '@/components/service/config-billing-cycle/v2/index';
import { isAutoAddable } from '@/plans/ZOOM_MEETING/common/utils';
import { showSyncAddonMessage } from '../mx/autoAdd';
import { capacityFormatter, capitalizeFirstLetter } from '@/common/utils';
import { getPlanUnitWithCycle, planUnitMap } from '@/common/product-unit';
import ConfigTextRow from '@/plans/EVENT_SAAS/component/config-text-row.vue';
import OpcOption from '@/components/zoom-ui-plus/select/option.vue';
import { BillingCycleKeysMapForAx } from '@/components/service/utils';

export const PLAN_UNIT_MAP = {
  license: 'license',
  user: 'user',
};

const splitCapacity = (value) => {
  const values = value?.split?.('-');
  if (values?.length) {
    const [capacity, billingCycle] = values;
    return [Number(capacity), billingCycle];
  }
  return [];
};

const combineCapacity = (planType, capacity, cycle) => {
  if (isEventSaasOrEventSassCore(planType, false)) {
    return `${capacity}-${cycle}`;
  }
  return capacity;
}

const setCapacityOptions = (options) => {
  if (Array.isArray(options)) {
    return options.map(o => {
      if (typeof o === 'number') {
        return {
          capacity: o,
        }
      }
      return o;
    })
  }
  return [options];
}

export default {
  name: 'ConfigRowV2',
  components: { ConfigCycle: ConfigCycleV2, SaveCount, InputNumberWithPopover, ConfigTextRow, OpcOption },
  props: {
    planType: String,
    // show alert warning or tooltips
    // true -> alert warning
    // false -> tooltips
    showCycleAlert: Boolean,
    showMonthlyPriceFromCycle: Boolean,
    // weather or not show capacity dropdown list
    showSelectCapacityDropdown: {
      type: Boolean,
      default: true,
    },
    capacityTips: String,
    licenseTips: String,
    selectCapacitySize: {
      type: String,
      default: 'large',
    },
    capacityUnitI18nKey: {
      type: String,
      default: 'billing.participants',
    },
    // plan unit (license/user/line)
    planUnit: {
      type: String,
      default: PLAN_UNIT_MAP.license,
      validator(value) {
        return Object.keys(PLAN_UNIT_MAP).includes(value);
      },
    },
    // current show capacity
    selectCapacity: {
      type: Number,
      required: true,
    },
    // capacity dropdown list
    capacityList: {
      type: Array,
      required: true,
      default: () => [],
    },
    // billing cycle plan list
    billingCycleList: {
      type: Array,
      required: true,
      default: () => [],
    },
    // capacity delete cta
    deleteVisible: {
      type: Boolean,
      default: true,
    },
    dividerVisible: {
      type: Boolean,
      default: true,
    },
    maxHosts: {
      type: Number,
      default: 999,
    },
    minHosts: {
      type: Number,
      default: 1,
    },
    decreaseTips: String,
    licenseAlert: String,
    beforeSwitchCycle: {
      type: Function,
      default: noopPromise,
    },
    getIncreaseTips: {
      type: Function,
      default: () => {
      },
    },
    unit: {
      type: Number,
      default: planUnitMap.license,
      validator: function(v) {
        return _.chain(planUnitMap)
          .values()
          .includes(v)
          .value();
      }
    },
    selectLabel: {
      type: String,
      default: '',
    },
    capacityGroupOptions: {
      type: Object,
      default: () => ({})
    },
    billingCycleGroupOptions: {
      type: Object,
      default: () => ({})
    },
    textViewModel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      license: 1,
      billingCycle: '',
    };
  },
  computed: {
    ...mapGetters([
      'syncBaseToSelectedPlanLicense',
      'getCurrentMeetingPlans',
      'getCurrentBasePlanInfo',
      'getSelectedPlans',
      'planMapFlat'
    ]),
    currentPlanList() {
      return _.map(this.billingCycleList, (p) => p.plan);
    },
    currentGroupPlanList() {
      return this.billingCycleGroupOptions.map(plan => {
        return plan.options.map(p => p.plan);
      }).flat();
    },
    cartSelectedPlan() {
      return _.find(this.currentPlanList, (p) => p?.cartInfo?.selected_number > 0);
    },
    trackingPlanInfo() {
      const planName = _.get(trackingPlanTypes, _.get(planTypes, this.planType));
      return {
        planName: planName,
        billingCycle: TrackingBillingCycleKeys[this.billingCycle],
        buySelection: `${TrackingBillingCycleKeys[this.billingCycle]} - ${this.selectCapacity} ${this.$t(this.capacityUnitI18nKey)}`
      }
    },
    planName() {
      return _.get(trackingPlanTypes, _.get(planTypes, this.planType));
    },
    trackingPlanKey() {
      return `${this.planName} ${this.selectCapacity ?? this.$t(this.capacityUnitI18nKey)} `;
    },
    selectedPlanCode() {
      return _.find(this.currentPlanList, (p) => p.billing_cycle === this.billingCycle)?.plan_code;
    },
    inputUnit() {
      return this.$t(`billing.planform.unit.${this.planUnit}`);
    },
    currentBasePlan() {
      const basePlans = this.getCurrentMeetingPlans.length ? this.getCurrentMeetingPlans : this.getCurrentBasePlanInfo?.plans;
      return _.get(basePlans, ['0'], {});
    },
    amountInfo() {
      const { amount = 0, billing_cycle, esmd_amount = 0 } = this?.cartSelectedPlan ?? {};
      return {
        amount: this.$money(amount + esmd_amount, { forceDiff: true }),
        cycleLabel: this.$t(billing_cycle ? getPlanUnitWithCycle(billing_cycle, this.unit) : '')
      }
    },
    allWebinarPlansList() {
      return this.planMapFlat.filter(p => p.type === this.planType);
    },
    selectedValue() {
      return combineCapacity(this.planType, this.selectCapacity, this.billingCycle);
    },
    firstDisabledCycleAlertItem() {
      return _.find(this.billingCycleList, (p) => p.disabled && !isEmptyVal(p.alert));
    },
  },
  methods: {
    ...mapActions(['switchCapacityAction']),
    ...mapMutations(['updateSelectPlan']),
    async switchBillingCycle(oldPlan, newPlan) {
      if (!newPlan) return;
      // when the current license less than max, get current license, otherwise get maxHosts
      const quantity = this.license <= this.maxHosts ? this.license : this.maxHosts;
      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };

      const preCartInfo = await this.beforeSwitchCycle(oldPlan, newPlanInfo);

      await this.switchCapacityAction([
        oldPlan?.plan_code,
        { ...newPlanInfo, cartInfo: { ...newPlanInfo.cartInfo, ...preCartInfo } },
      ]);

      this.updateVisuallyHiddenTextContent(newPlanInfo.billing_cycle, newPlanInfo.capacity);

      // event tracking
      const billingCycle = this.cartSelectedPlan?.billing_cycle;
      if (this.$context?.is.zs2ze) {
        billingCycle && this.$tracking.buyInteraction(`zoom events plan`, billingCycle);
      } else {
        billingCycle && this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey} billing cycle`,
          this.$t(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
        );
      }
    },
    updateCart(newLicense, oldLicense) {
      const planCode = this.selectedPlanCode;

      if (planCode) {
        this.updateSelectPlan({
          plan_code: planCode,
          cartInfo: { selected_number: newLicense },
        });
      }

      this.$nextTick(() => {
        // event tracking
        if (this.$context?.is.zs2ze) {
          planCode && this.$tracking.buyInteraction(`zoom events plan license`,
            `${this.billingCycle} - ${this.selectCapacity} attendees - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
        } else if ([planTypes.WEBINAR].includes(this.planType)) {
          planCode && this.$tracking.buyInteraction(`${this.trackingPlanInfo.planName}`,
            `${this.trackingPlanInfo.buySelection} - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
        } else {
          planCode && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} license`,
            `${this.trackingPlanKey} - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
        }
      });
    },
    changeLicense(newLicense) {
      const oldLicense = this.license;
      this.license = newLicense;
      const payload = [newLicense, oldLicense];

      if (this.updateShoppingCart) {
        this.updateShoppingCart(...payload);
      } else {
        this.updateCart(...payload);
      }
    },
    onSwitchCapacity(capacity, billingCycle) {
      const isFromTabKeyboard = document.activeElement.tagName === 'DL' && document.activeElement.getAttribute('role') === 'listbox';
      const { planName } = this.trackingPlanInfo;
      const { planType, trackingPlanKey } = this;
      const [_capacity, _billingCycle] = splitCapacity(capacity);
      capacity = _capacity || capacity;
      billingCycle = _billingCycle || billingCycle;
      this.$emit('switch-capacity', this.selectCapacity, capacity, this.license, isFromTabKeyboard, billingCycle, (newPlanInfo) => {
        this.$nextTick(() => {
          this.updateVisuallyHiddenTextContent(newPlanInfo.billing_cycle, newPlanInfo.capacity);
          if (this.$context.is.zs2ze) {
            this.$tracking.buyInteraction(`zoom events attendees`, `${TrackingBillingCycleKeys[newPlanInfo.billing_cycle]} - ${capacity} attendees/year`);
          } else if ([planTypes.WEBINAR].includes(planType)) {
            this.$tracking.buyInteraction(`${planName}`, `${TrackingBillingCycleKeys[newPlanInfo.billing_cycle]} - ${capacity} attendees${TrackingBillingCycleUnitKeys[newPlanInfo.billing_cycle]}`);
          } else {
            this.$tracking.addOnBuyInteraction(`${trackingPlanKey} attendees`, capacity);
          }
        });
      });
    },
    onDelete() {
      this.deleteVisible && this.$emit('delete-capacity', this.selectedPlanCode);
      if (this.billingCycle) {
        this.$tracking.buyInteraction(`${this.trackingPlanInfo.planName}`, `${this.trackingPlanInfo.buySelection} - remove`);
      } else {
        this.$tracking.buyInteraction(`${this.trackingPlanKey} plan`, `remove`);
      }
    },
    focusSelect() {
      this.$refs.attendeeSelect?.focus();
    },
    toggleLicenseTips(tips) {
      tips && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} options`, 'licenses - tooltip');
    },
    changeBillingCycle(cycle, _, newPlan) {
      this.billingCycle = cycle;

      // just for webinar
      if (!newPlan?.capacity && this.planType === planTypes.WEBINAR) {
        // change capacity
        const isCapacitySupported =  this.billingCycleList.find(p => {
          const { plan } = p;
          return plan.billing_cycle === cycle;
        });
        // may be never run this code?
        if (!isCapacitySupported) {
          const availablePlans = this.allWebinarPlansList.filter(p => {
            const isSelected = this.getSelectedPlans.find(plan => p.plan_code === plan.plan_code || p.capacity === plan.capacity);
            if (p.billing_cycle === cycle && !isSelected) return true;
            return false;
          }).sort((x, y) => {
            return x.capacity - y.capacity;
          });
          if (availablePlans.length) {
            this.onSwitchCapacity(availablePlans[0].capacity, availablePlans[0].billing_cycle);
          }
        }
      }

      this.$emit('switch-cycle', cycle, this.selectCapacity, newPlan.capacity);
    },
    toggleCapacityTips(tips) {
      if (this.$context?.is.zs2ze) {
        tips && this.$tracking.buyInteraction(`zoom events options`, 'select number of attendees - tooltip');
      } else {
        tips && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} options`, 'licenses - tooltip');
      }
    },
    mx_renderCapacityDropdown() {
      const unitName = capitalizeFirstLetter(this.$t(this.capacityUnitI18nKey));
      const groupOptions = this.capacityGroupOptions.filter(group => group?.options?.length);
      const label = this.selectLabel || `${this.planName} ${this.$t(this.capacityUnitI18nKey)}`;

      return <div class='opc-capacity-config-row__capacity--wrap'>
        <zm-select
          data-testid="checkout-select-11"
          ref='attendeeSelect'
          popper-append-to-body={true}
          inline-label={unitName}
          size={this.selectCapacitySize}
          value={this.selectedValue}
          label={label}
          onInput={this.onSwitchCapacity}
          popper-class='opc-capacity-config-row__capacity--popper'
          class='opc-capacity-config-row__capacity--select'>
        {
          groupOptions.map(group => {
            return <zm-option-group
              key={group.label}
              label={group.label}
              >
              {setCapacityOptions(group.options).map((p) => {
                  return <opc-option
                    key={combineCapacity(this.planType, p.capacity, group.cycle)}
                    value={combineCapacity(this.planType, p.capacity, group.cycle)}
                    label={capacityFormatter(p.capacity)}
                    disabled={p.disabled}
                    tooltip-content={p.tooltips}
                  />;
                })}
              </zm-option-group>
          })
        }

        </zm-select>
      </div>;
    },
    mx_renderCapacityView() {
      const unitName = this.$t(this.capacityUnitI18nKey);
      return <div class='opc-capacity-config-row__cycle__title'>
        {this.selectCapacity} <span class='opc-capacity-config-row__capacity--unit'>{unitName}</span>
      </div>;
    },
    mx_renderTextViewModel() {
      const unitName = this.$t(this.capacityUnitI18nKey);
      return <config-text-row title={capitalizeFirstLetter(unitName)} value={this.selectCapacity} />
    },
    mx_renderCapacity() {
      return <div class='opc-capacity-config-row__capacity'>
        {
          this.textViewModel ? this.mx_renderTextViewModel() :
            this.showSelectCapacityDropdown ?
              this.mx_renderCapacityDropdown() :
              this.mx_renderCapacityView()
        }
        {(this.$slots.capacityTips || !this.capacityTips) ?
          this.$slots.capacityTips :
          <zm-tooltip
            class='opc-capacity-config-row__tooltips'
            role='button'
            aria-label='Learn more about Select the number of licenses'
            content={this.capacityTips}
            placement='bottom'
            onInput={this.toggleCapacityTips}>
            <i class='zm-icon-info-outline' />
          </zm-tooltip>}
      </div>;
    },
    mx_renderLicense() {
      return <div class='opc-capacity-config-row__input__number'>
        <input-number-with-popover
          value={this.license}
          onChange={this.changeLicense}
          size='large'
          controls-position='both'
          min={this.minHosts}
          max={this.maxHosts}
          increaseTips={this.getIncreaseTips(this.selectCapacity, this.license)}
          decreaseTips={this.decreaseTips}
          unit={this.inputUnit}
          label={`${this.trackingPlanKey} + Select the number of ${this.planUnit}`}
        />
        {/* <span
          class="opc-capacity-config-row__input__number--unit">{this.$t(`billing.planform.unit.${this.planUnit}`)}</span> */}
        {(this.$slots.licenseTips || !this.licenseTips) ?
          this.$slots.licenseTips :
          <zm-tooltip
            class='opc-capacity-config-row__tooltips'
            role='button'
            aria-label='Learn more about Select the number of licenses'
            content={this.licenseTips}
            placement='bottom'
            onInput={this.toggleLicenseTips}>
            <i class='zm-icon-info-outline' />
          </zm-tooltip>}
      </div>;
    },
    mx_renderRemoveBtn() {
      return <zm-button
        data-testid="checkout-button-12"
        type='link'
        ghost
        size='mini'
        onClick={this.onDelete}
        aria-label={`remove ${this.trackingPlanKey}`}
        class={{
          'opc-capacity-config-row__delete-btn': true,
          'opc-capacity-config-row__delete--btn': !this.deleteVisible,
        }}
      >
        <i class='opc-capacity-config-row__delete--icon zm-icon-close' />
      </zm-button>;
    },
    mx_renderAlert(slot, title) {
      if (slot) return slot;
      if (title) return <zm-alert
        class='opc-common-info-alert'
        type='warning'
        closable={false}
        show-icon>
        <p domPropsInnerHTML={title} onClick={this.trackingAlert} />
      </zm-alert>;
    },
    mx_renderDivider() {
      return this.dividerVisible ? <hr /> : null;
    },
    mx_renderCycleRow() {
      const groupOptions = this.billingCycleGroupOptions.filter(group => group?.options?.length);
      const textViewModel = isEventSaasOrEventSassCore(this.planType, false) && groupOptions.length <= 1;
      return <config-cycle
        ref='configCycle'
        billingCycle={this.billingCycle}
        billingCycleList={this.billingCycleList}
        billingCycleGroupOptions={groupOptions}
        onSwitchCycleAction={this.switchBillingCycle}
        showMonthlyPriceFromCycle={this.showMonthlyPriceFromCycle}
        onSyncCycle={this.changeBillingCycle}
        plan-name={this.planName}
        textViewModel={textViewModel}
      />;
    },
    mx_renderAmount() {
      return (<p class='opc-cycle__plan__price'>
              <span class='opc-cycle__plan__price--amount'>
                {this.amountInfo.amount}
              </span>
              <span class='opc-cycle__plan__price--unit'>
                {this.amountInfo.cycleLabel}<priceVATTip />
              </span>
      </p>);
    },
    mx_renderCycleDisabledAlert() {
      if (!this.showCycleAlert || !this.firstDisabledCycleAlertItem) return null;
      return this.mx_renderAlert(this.$slots?.cycleAlert, this.firstDisabledCycleAlertItem?.alert);
    },
    trackingAlert(e) {
      const { tagName = "", className, text = "" } = e?.target || {};
      const { alert } = this.firstDisabledCycleAlertItem || {};

      if (
        tagName?.toLowerCase() === "a"
        && className === "opc-go-back__billing"
        && this.showCycleAlert
        && alert
      ) {
        this.$tracking.buyInteraction(
          `banner - product level`,
          `${this.trackingPlanName ? `${this.trackingPlanName} - `: ''}${alert} - ${text}`,
        );
      }
    },
    updateVisuallyHiddenTextContent(billingCycle, capacity) {
      this.$nextTick(() => {
        const visuallyHiddenEle = document.querySelector('.visually-hidden');
        if (visuallyHiddenEle && billingCycle && capacity) {
          visuallyHiddenEle.textContent = `Changed to ${BillingCycleKeysMapForAx?.[billingCycle]} ${capacity} attendees`;
        }
      });
    }
  },
  mounted() {
    // fix ax issue
    this.$refs.configCycle?.focus?.();
  },
  render() {
    return <div class='opc-capacity-config-row opc-capacity-config-row-v2'>
      <div class='opc-capacity-config-row__license'>
        <div class='opc-capacity-config-row__license__content'>
          <div class='opc-capacity-config-row__license__content--cycle'>
            {this.mx_renderCapacity()}
            {this.mx_renderCycleRow()}
          </div>
          {this.mx_renderLicense()}
        </div>
        {this.mx_renderRemoveBtn()}
      </div>
      {this.mx_renderAmount()}
      {this.mx_renderAlert(this.$slots.licenseAlert, this.licenseAlert)}
      {this.mx_renderCycleDisabledAlert()}
      {this.mx_renderDivider()}
    </div>;
  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan, oldPlan) {
        if (
          plan?.cartInfo?.selected_number === oldPlan?.cartInfo?.selected_number &&
          plan?.billing_cycle === oldPlan?.billing_cycle
        ) {
          return;
        }
        // sync shopping cart form state to local form state
        this.billingCycle = plan?.billing_cycle ?? '';
        this.license = plan?.cartInfo?.selected_number || 1;
      },
    },
    license: {
      immediate: true,
      handler(newLicense, oldLicense) {
        this.$emit(
          'change-license',
          newLicense,
          oldLicense,
          { capacity: this.selectCapacity, billingCycle: this.billingCycle },
          this._uid,
        );
      },
    },
    maxHosts: {
      immediate: true,
      handler(nv) {
        const oldLicense = this.license;
        if (this.license > nv) {
          this.changeLicense(nv);
          // when auto add enable, decrease of base plan license is no longer limited
          // current plan license will change according to the maxHosts(base plan license)
          // show notification
          const planNameKey = this.currentPlanList.find((plan) => plan.type === this.planType)?.planNameKey;
          showSyncAddonMessage(planNameKey, this.currentBasePlan?.planNameKey);
        }
        this.$emit(
          'change-license',
          this.license,
          oldLicense,
          { capacity: this.selectCapacity, billingCycle: this.billingCycle },
          this._uid,
        );
      },
    },
    // Auto add
    // @jira https://zoomvideo.atlassian.net/browse/OBT-2083
    // watch base plan license change to trigger current plan license change
    syncBaseToSelectedPlanLicense: {
      immediate: false,
      handler(nv) {
        if (!nv) return;
        if (!(isAutoAddable() && autoAddTypes.includes(this.planType))) {
          return;
        }
        // not include one-time plan
        if (this.cartSelectedPlan?.billing_cycle === BILLING_CYCLE.ONE_TIME) return;
        const { baseLicense } = nv;
        if (baseLicense < this.license) {
          this.changeLicense(baseLicense);
          const planNameKey = this.currentPlanList.find((plan) => plan.type === this.planType)?.planNameKey;
          showSyncAddonMessage(planNameKey, this.currentBasePlan?.planNameKey);
        }
      },
    },
    firstDisabledCycleAlertItem: {
      immediate: true,
      handler(item) {
        if (this.showCycleAlert && item?.alert) {
          this.$tracking.buyInteraction(
            'banner - product level',
            `${this.trackingPlanName ? `${this.trackingPlanName} - `: ''}${item.alert} - impression`,
          );
        }
      },
    }
  },
  created() {
    // A closure (timeId) needs to be generated for each config row instance
    this.updateShoppingCart = _.debounce(this.updateCart, 200);
  },
};
</script>

<style lang='scss'>
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/scss-utils.scss';

.opc-capacity-config-row__capacity--popper {
  .zm-select-dropdown__wrap.zm-scrollbar__wrap {
    max-height: unset !important;
  }
  .zm-select-group__title {
    padding-left: 8px;
  }
  .zm-select-group__wrap:not(:last-of-type)::after {
    left: 8px;
    right: 8px;
  }
}

.opc-capacity-config-row.opc-capacity-config-row-v2 {
  .opc-cycle__plan__price {
    margin-top: 16px;
  }

  .zm-select .zm-select-input.zm-select--show-label {
    padding-top: 20px;
  }

  .zm-select .zm-select-input.is-focused .zm-select__label {
    line-height: 16px;
  }

  .zm-select.zm-select--xLarge .zm-select--show-label .zm-select-span__inner,
  .zm-select.zm-select--xLarge .zm-select--show-label .zm-select-input__inner {
    line-height: 24px;
    font-size: 18px;
  }

  .zm-select .zm-select-input.has-slot {
    @include media(mobile) {
      flex-wrap: wrap;
    }
  }

  .zm-select.zm-select--xLarge .zm-select-input {
    $caret-size: 16px;
    $caret-margin: 8px;
    padding-right: $caret-size + $caret-margin * 2;
    .zm-select-toggle {
      right: $caret-margin;
    }
    .zm-select__caret {
      width: $caret-size;
      height: $caret-size;
      line-height: $caret-size;
      font-size: $caret-size;
    }
  }

  .opc-capacity-config-row__input__number {
    margin-top: 16px;
  }

  .opc-capacity-config-row__capacity {
    &--select {
      width: 140px;
      height: 48px;

      @include media(mobile) {
        width: 200px;
      }

      .zm-select-input {
        .zm-select-span {
          &__inner {
            font-weight: 700;
            font-size: 18px;
            color: var(--zoom-color-blue-default, #0B5CFF);
          }
        }
      }
    }
  }

}

.opc-capacity-config-row {

  &__tooltips {
    color: #0B5CFF;
    font-size: 14px;
    margin-left: 4px;
    cursor: pointer;
  }

  &__license {
    display: flex;
    justify-content: space-between;
    @include media(mobile) {
      position: relative;
      flex-wrap: wrap;
    }

    &__content {
      display: flex;
      flex-direction: column;

      &--cycle {
        display: flex;
        align-items: center;

        @include media(mobile) {
          flex-direction: column;
          align-items: flex-start;
        }
      }
    }
  }

  &__input__number {
    @include media(mobile) {
      margin-top: 12px;
      width: 100%;
    }

    &--unit {
      margin-left: 8px;
    }
  }

  $capacityHeight: 40px;

  &__delete-btn.zm-button {
    @include media(mobile) {
      position: absolute;
      top: $capacityHeight / 2;
      right: 0;
      transform: translateY(-50%);
    }
  }

  &__delete {
    &--btn {
      visibility: hidden;
    }

    &--icon {
      color: #6E7680;
      font-size: 16px;
    }
  }

  &__capacity {
    display: flex;
    align-items: center;
    @include media(mobile) {
      padding-right: $capacityHeight;
      width: 100%;
    }

    &--unit {
      margin-left: 8px;
      text-transform: capitalize;
    }
  }

  &__cycle {
    margin-top: 16px;

    &__title {
      display: flex;
      align-items: center;
      font-weight: 700;
      font-size: 18px;
      line-height: 150%;
      color: #00053D;
      height: 100%;
    }

    &__plan {
      margin-top: 16px;

      &__title {
        font-weight: 700;
        font-size: 16px;
        text-transform: capitalize;
        display: flex;
        align-items: center;
        flex-wrap: wrap; // for i18n
      }

      &__price {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        margin-top: 16px;

        &--amount, &--unit {
          color: #00053D;
          line-height: 150%;
          @include darkTextColor;
        }

        &--amount {
          font-weight: 700;
          font-size: 22px;
          word-break: break-all;
        }

        &--unit {
          font-weight: 500;
          font-size: 12px;
          color: #515079;
          word-break: break-all;
          @include darkTextColor2;
        }
      }
    }

    &__disabled--desc {
      font-family: Almaden Sans;
      font-size: 14px;
      font-weight: 500;
      line-height: 21px;
      letter-spacing: 0em;
      text-align: left;
      color: #515079;
      margin-top: 8px;
      @include darkTextDisable;
    }
  }
}

.opc-warning-message {
  display: flex;
  align-items: center;
  background: #FEF7D9;
  color: #815A30;

  &__addon {
    margin-top: 16px;
  }
}
</style>
